import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useWageBreakdownReturn } from "../../../components/WageBreakdownReturn/useWageBreakdownReturn";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useArrivalState } from "../../../hooks/useArrivalState";
import { backendService } from "../../../services/backend.service";
import { trackErrorEvent, trackEvent } from "../../../utils/analytics";
import {
	type DayOff,
	type EditableNurse,
	EmploymentType,
	type NurseFullRole,
	NurseRole,
	NurseTrainingStatus,
	ProfileMode,
	ShiftType,
	WageType,
} from "../../../utils/models";
import type { NurseMergeResult } from "../../../utils/nurseMergeModels";
import { FROM_WAGE_BREAKDOWN_STATE_KEY, parseWageBreakdownOrigin } from "../../../utils/navigation/wageBreakdownReturn";
import { isAgencyNurse, normalizeUnionName, rolesMap } from "../../../utils/utilFunctions";
import { emailValidationCallback, partialNameValidationCallback, phoneNumberValidationCallback } from "./GeneralDetails/useGeneralProfileDetailsState";
import {
	type DuplicateExistingNurseMatch,
	useAddNewNurseProfile,
	useCheckDuplicateNurse,
	useDeleteDayOff,
	useEditNurseProfile,
	useGetNurseProfile,
	useGetNurseProfileImage,
	usePostProfileImage,
} from "./hooks";
import { getSanitizedNurseData } from "./util/constants";

type SanitizedNurseData = NonNullable<ReturnType<typeof getSanitizedNurseData>>;

type PendingJobChange = {
	removedJobIds: string[];
	/** first entry is the new primary — the job blocked shifts would move to. */
	newJobIds: string[];
	/** held so the dialog's apply can finish the save the 409 interrupted. */
	nurseDataToSubmit: SanitizedNurseData;
};

type Props = {
	initialProfileMode: ProfileMode;
};
export const useProfileV2State = ({ initialProfileMode }: Props) => {
	const { chosenDepartment, departmentNurseTypes, addNewDBNurseShifts, removeNursesShiftsCompletely, isAnyDemo, refreshNursesData, showErrorSnackbar } =
		useHospitalData();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();
	const teamPath = `/${chosenDepartment.department_id}/team`;
	// DEFAULT: a profile can be open with no entry point to go back to — a pasted URL, a reload, or a
	// switch from such a profile (APP-535) — so Back still needs a destination. Team is where profiles
	// are listed, and it only exists under a department: a bare "/team" is not a route and dead-ends.
	const navigationSrc = (location.state?.navigationSrc as string | undefined) ?? teamPath;
	// DAT-906 · Opened from a shift's wage breakdown: Back returns to it, with the breakdown open again.
	// Reset per nurse — this page is not remounted when switching to another employee.
	const breakdownOrigin = useArrivalState(FROM_WAGE_BREAKDOWN_STATE_KEY, parseWageBreakdownOrigin, id ?? null);
	const returnToWageBreakdown = useWageBreakdownReturn(breakdownOrigin);

	const departmentDefaultRole = chosenDepartment.default_role ? rolesMap[chosenDepartment.default_role] : undefined;

	const [nurseData, setNurseData] = useState<EditableNurse>({
		firstName: "",
		lastName: "",
		employmentType: EmploymentType.FullTime,
		extraShifts: false,
		departments: [chosenDepartment.department_id],
		email: "",
		qualifications: [],
		gender: "",
		phoneNumber: "",
		maritalStatus: "",
		country: "",
		city: "",
		street: "",
		zipCode: "",
		role: departmentDefaultRole,
		status: "",
		allowedShifts: 3,
		notSchedule: [],
		profileImage: "",
		shiftsNotWorking: [],
		idNumber: "",
		employeeWeeklyPreferences: [],
		isOnSchedule: true,
		reliabilityScore: 0.8,
		isShiftManager: false,
		hourlyWage: undefined,
		employeeSpecificPreferences: [],
		id: 0,
		shiftType: ShiftType.All,
		canBeReliefCharge: false,
		weekendShiftFlexibility: false,
		shiftConstraints: {},
		trainingStatus: NurseTrainingStatus.Regular,
		wage: undefined,
		wageType: WageType.Hourly,
		baseWages: [],
		lastLoginTime: undefined,
		doNotDisturbCalling: false,
		homeDepartmentId: undefined,
	});
	const [preEditNurseData, setPreEditNurseData] = useState<EditableNurse>(nurseData);
	// The last copy the server handed the form (a profile fetch or a save's echo) — what "unsaved edits" is
	// measured against. Not `preEditNurseData`: controls that save on their own (the status change) refetch
	// the profile mid-edit, and that refetch is not an edit.
	const [lastSeededNurseData, setLastSeededNurseData] = useState<EditableNurse>(nurseData);
	const [pendingJobChange, setPendingJobChange] = useState<PendingJobChange | null>(null);
	const [isApplyingJobChange, setIsApplyingJobChange] = useState(false);
	const [createUserWhenCreatingNurse, setCreateUserWhenCreatingNurse] = useState(false);
	const [disableSaveDueToAnIssueCreatingUser, setDisableSaveDueToAnIssueCreatingUser] = useState(false);
	const [profileMode, setProfileMode] = useState<ProfileMode>(initialProfileMode);
	const [showEditCancelWarning, setShowEditCancelWarning] = useState(false);
	const isEditable = useMemo(() => profileMode !== ProfileMode.VIEW, [profileMode]);
	const [changedImage, setChangedImage] = useState<File | null>(null);
	const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
	const [welcomeMessage, setWelcomeMessage] = useState("");
	// Suspects shown in the duplicate-confirm dialog.
	const [duplicateMatches, setDuplicateMatches] = useState<DuplicateExistingNurseMatch[]>([]);

	const { isErrorProfile, isLoadingProfile, profileData, errorProfile, refetchProfile } = useGetNurseProfile({
		department_id: chosenDepartment?.department_id,
		nurse_id: id,
	});
	const { imageDataString } = useGetNurseProfileImage({ nurse_id: id ?? "" });

	const { deleteDayOffMutateAsync, isPending: isDeletingTimeOff } = useDeleteDayOff({ addNewDBNurseShifts, removeNursesShiftsCompletely, refetchProfile });
	const { addNurseProfileAsync, isAddingNurseLoading } = useAddNewNurseProfile();
	const { mutateAsync: checkDuplicateNurseAsync } = useCheckDuplicateNurse();
	const { isEditNurseLoading, editNurseProfileAsync } = useEditNurseProfile();
	const { isPostProfileImageLoading, postProfileImageAsync } = usePostProfileImage();

	useEffect(() => {
		if (!isErrorProfile && profileData) {
			const seeded = { ...profileData, union: normalizeUnionName(profileData.union) };
			setNurseData(seeded);
			setLastSeededNurseData(seeded);
		}
	}, [profileData, isErrorProfile]);

	/**
	 * True while the employee held in state is not yet the one the route asks for. Switching profiles
	 * (APP-535) changes `:id` without remounting the page, so until the new profile lands `nurseData`
	 * is still the *previous* employee — acting on "this employee" then would hit the wrong person.
	 * Comparing against the route id (and not just the query's loading flag) also covers the render
	 * where the fetch has resolved but the effect above has not yet copied it into state.
	 */
	const isProfileHydrating = profileMode !== ProfileMode.NEW && (isLoadingProfile || String(nurseData.id) !== id);

	useEffect(() => {
		if (isErrorProfile && errorProfile && initialProfileMode === ProfileMode.VIEW) {
			const axiosError = errorProfile as AxiosError;
			if (axiosError.response?.status === 403 && (axiosError.response?.data as { detail?: string })?.detail === "Nurse not exists in department") {
				showErrorSnackbar(`Nurse with id ${id} does not exist in this department`, {
					department_id: chosenDepartment?.department_id,
					nurse_id: id,
				});
				navigate(teamPath, { replace: true });
			}
		}
	}, [isErrorProfile, errorProfile, initialProfileMode, chosenDepartment?.department_id, id, navigate, showErrorSnackbar, teamPath]);

	useEffect(() => {
		if (profileMode !== ProfileMode.NEW) {
			return;
		}
		const defaultRole = chosenDepartment.default_role ? rolesMap[chosenDepartment.default_role] : undefined;
		if (!defaultRole) {
			return;
		}
		setNurseData((prev) => {
			if (prev.role === defaultRole) {
				return prev;
			}
			if (prev.role !== rolesMap[NurseRole.RN]) {
				return prev;
			}
			return {
				...prev,
				role: defaultRole,
			};
		});
	}, [chosenDepartment.default_role, profileMode]);

	useEffect(() => {
		if (profileMode === ProfileMode.NEW && nurseData?.employmentType) {
			if (isAgencyNurse(nurseData.employmentType)) {
				setCreateUserWhenCreatingNurse(false);
			} else if (
				(nurseData?.phoneNumber && phoneNumberValidationCallback(nurseData.phoneNumber)) ||
				(nurseData?.email && emailValidationCallback(nurseData.email))
			) {
				setCreateUserWhenCreatingNurse(true);
			}
		}
	}, [nurseData?.employmentType, nurseData?.phoneNumber, nurseData?.email, profileMode]);

	const onDeletePtoConfirmed = useCallback(
		async (dayOff: DayOff, restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => {
			await deleteDayOffMutateAsync({
				dayOff: dayOff,
				nurseId: nurseData.id,
				departmentId: chosenDepartment.department_id,
				restoreRecurringShifts,
				restoreTrimmedShifts,
			});
		},
		[nurseData, deleteDayOffMutateAsync, chosenDepartment.department_id],
	);

	const toggleEditCancelWarning = useCallback(() => {
		setShowEditCancelWarning((prev) => !prev);
	}, []);

	const goBackToSource = useCallback(() => {
		navigate(navigationSrc);
	}, [navigate, navigationSrc]);

	const handleOnBackClick = useCallback(() => {
		trackEvent("profile_back_click", {
			nurseId: nurseData.id,
			mode: profileMode,
		});
		if (profileMode !== ProfileMode.VIEW) {
			toggleEditCancelWarning();
		} else if (returnToWageBreakdown) {
			returnToWageBreakdown();
		} else {
			goBackToSource();
		}
	}, [nurseData.id, profileMode, toggleEditCancelWarning, goBackToSource, returnToWageBreakdown]);
	// View mode only: in edit mode Back asks about unsaved changes, whatever the way in.
	const backLabel = profileMode === ProfileMode.VIEW && returnToWageBreakdown ? t("wageBreakdown.return") : t("back");

	const onEditClick = useCallback(() => {
		setPreEditNurseData({ ...nurseData });
		setProfileMode(ProfileMode.EDIT);
		trackEvent("profile_edit_click", {
			nurseId: nurseData.id,
			nurseName: `${nurseData.firstName} ${nurseData.lastName}`,
		});
	}, [nurseData]);

	const handleEditCancelConfirmed = useCallback(() => {
		if (profileMode === ProfileMode.NEW) {
			navigate(-1);
		} else {
			setProfileMode(ProfileMode.VIEW);
			toggleEditCancelWarning();
			setNurseData(preEditNurseData);
			trackEvent("profile_edit_cancel", {
				nurseId: preEditNurseData.id,
				nurseName: `${preEditNurseData.firstName} ${preEditNurseData.lastName}`,
			});
		}
	}, [toggleEditCancelWarning, preEditNurseData, profileMode, navigate]);

	const isFormValid = useMemo(() => {
		if (!nurseData || (createUserWhenCreatingNurse && disableSaveDueToAnIssueCreatingUser)) {
			return false;
		}
		const nurseDataToSubmit = getSanitizedNurseData(nurseData);
		if (!nurseDataToSubmit) {
			return false;
		}

		// Block save when the stored role isn't one this department offers (e.g. a legacy
		// UDF nurse in an LCSW-only dept). Without this, the invalid role persists unchanged:
		// the role dropdown shows nothing selected but nurseData.role stays the stale value,
		// so re-saving silently rewrites the old role (DAT-508). Forcing a valid pick first
		// makes the change actually stick.
		if (!nurseDataToSubmit.role || !departmentNurseTypes.includes(nurseDataToSubmit.role as NurseFullRole)) {
			return false;
		}

		if (nurseDataToSubmit.phoneNumber === undefined || nurseDataToSubmit.wage === undefined || nurseDataToSubmit.email === undefined) {
			trackErrorEvent("sanitize_nurse_error", {
				initialNurseData: nurseData,
				sanitizedNurseData: nurseDataToSubmit,
			});
			console.error("sanitize_nurse_error", {
				initialNurseData: nurseData,
				sanitizedNurseData: nurseDataToSubmit,
			});
			return false;
		}
		return (
			partialNameValidationCallback(nurseDataToSubmit.firstName) &&
			partialNameValidationCallback(nurseDataToSubmit.lastName) &&
			phoneNumberValidationCallback(nurseDataToSubmit.phoneNumber) &&
			emailValidationCallback(nurseDataToSubmit.email) &&
			nurseDataToSubmit.wage >= 0
		);
	}, [nurseData, createUserWhenCreatingNurse, disableSaveDueToAnIssueCreatingUser, departmentNurseTypes]);

	useEffect(() => {
		if (!isEditable || departmentNurseTypes.length === 0) {
			return;
		}
		if (nurseData.role && !departmentNurseTypes.includes(nurseData.role as NurseFullRole)) {
			showSnackbar(t("profile.role.invalidForDepartment"), "warning");
		}
	}, [isEditable, nurseData.role, departmentNurseTypes, showSnackbar, t]);

	const createNurseAndNavigate = useCallback(
		async (nurseDataToSubmit: ReturnType<typeof getSanitizedNurseData>) => {
			if (!nurseDataToSubmit) return;
			try {
				const newNurseId = await addNurseProfileAsync({
					nurseObjectToSubmit: {
						...nurseDataToSubmit,
						role: nurseDataToSubmit.role as NurseFullRole,
					},
					shouldCreateUser: createUserWhenCreatingNurse && !disableSaveDueToAnIssueCreatingUser,
					shouldNotify: true,
				});
				if (newNurseId) {
					navigate(`/${chosenDepartment.department_id}/nurse/${newNurseId}`);
				}
				await refreshNursesData(chosenDepartment.department_id);
			} catch {
				/* error is snackbar'd by the hook */
			}
		},
		[addNurseProfileAsync, createUserWhenCreatingNurse, disableSaveDueToAnIssueCreatingUser, refreshNursesData, chosenDepartment.department_id, navigate],
	);

	/**
	 * Persists everything on the profile form except the job list, which `PUT /jobs`
	 * owns. Shared with the blocked dialog's apply so resolving a job conflict doesn't
	 * discard the other edits.
	 */
	const saveProfileEdits = useCallback(
		async (nurseDataToSubmit: SanitizedNurseData) => {
			let uploadedImageName = nurseDataToSubmit.profileImage;
			if (changedImage) {
				uploadedImageName = await postProfileImageAsync({
					imageFile: changedImage,
					nurseId: nurseDataToSubmit.id,
				});
			}
			try {
				const updatedNurse = await editNurseProfileAsync({
					nurseObjectToEdit: {
						...nurseDataToSubmit,
						profileImage: uploadedImageName,
						role: nurseDataToSubmit.role as NurseFullRole,
					},
				});
				if (updatedNurse) {
					// The second seeding point, and it needs the same normalization as the first: saving
					// invalidates only the cost queries, never ["nurseProfile"], so nothing re-hydrates
					// afterwards — this raw PUT echo IS the edit state, and `onEditClick` snapshots it into
					// `preEditNurseData`. Normalizing here keeps the invariant a frontend one instead of
					// resting on the backend happening to echo a normalized value (APP-13).
					const seeded = { ...updatedNurse, union: normalizeUnionName(updatedNurse.union) };
					setNurseData(seeded);
					setLastSeededNurseData(seeded);
					setProfileMode(ProfileMode.VIEW);
				}
			} catch {
				/*error handling is done by the editNurseProfileAsync hook*/
			}
			await refreshNursesData(chosenDepartment.department_id);
		},
		[changedImage, postProfileImageAsync, editNurseProfileAsync, refreshNursesData, chosenDepartment.department_id],
	);

	const handleSaveClick = useCallback(async () => {
		trackEvent("profile_save_click", {
			nurseId: nurseData.id,
			nurseName: `${nurseData.firstName} ${nurseData.lastName}`,
		});
		if (isFormValid) {
			const nurseDataToSubmit = getSanitizedNurseData(nurseData);
			if (!nurseDataToSubmit) {
				trackErrorEvent("sanitize_nurse_error", {
					initialNurseData: nurseData,
					sanitizedNurseData: nurseDataToSubmit,
				});
				console.error("sanitize_nurse_error", {
					initialNurseData: nurseData,
					sanitizedNurseData: nurseDataToSubmit,
				});
				return;
			}
			if (profileMode === ProfileMode.NEW) {
				// Read-only guard: flag same-name, no-shift nurses so the scheduler can confirm first.
				const matched = await checkDuplicateNurseAsync({
					firstName: nurseDataToSubmit.firstName,
					lastName: nurseDataToSubmit.lastName,
					departmentId: chosenDepartment.department_id,
				}).catch(() => [] as DuplicateExistingNurseMatch[]);
				if (matched.length > 0) {
					trackEvent("duplicate_nurse_warning_shown", {
						matched_nurse_ids: matched.map((m) => m.id),
						count: matched.length,
						attempted_first_name: nurseDataToSubmit.firstName,
						attempted_last_name: nurseDataToSubmit.lastName,
					});
					setDuplicateMatches(matched);
					return;
				}
				await createNurseAndNavigate(nurseDataToSubmit);
				return;
			}
			if (profileMode === ProfileMode.EDIT) {
				// Job removal flow (INH-3268, INH-3294): PUT /jobs is the only guard — it
				// 409s on future shifts under a removed job and we offer the one-step fix
				// (DAT-582), rather than deciding here from possibly-stale cached data.
				const originalJobIds = preEditNurseData.jobIds ?? [];
				const newJobIds = nurseDataToSubmit.jobIds ?? [];
				const removedJobIds = originalJobIds.filter((id) => !newJobIds.includes(id));
				if (removedJobIds.length > 0) {
					try {
						await backendService.updateNurseJobs(chosenDepartment.department_id, nurseData.id, {
							new_job_ids: newJobIds,
							reassign_shifts_to_primary: false,
						});
						track("nurse_job_change", { removedJobCount: removedJobIds.length, reassignedShifts: false });
						await refreshRequests();
					} catch (e) {
						const responseStatus = (e as AxiosError)?.response?.status;
						if (responseStatus === 409) {
							setPendingJobChange({ removedJobIds, newJobIds, nurseDataToSubmit });
							return;
						}
						trackErrorEvent("nurse_job_change_error", { nurseId: nurseData.id, error: String(e) });
						showErrorSnackbar("error.general", { nurseId: nurseData.id }, e);
						return;
					}
				}

				await saveProfileEdits(nurseDataToSubmit);
			}
		}
	}, [
		isFormValid,
		nurseData,
		createNurseAndNavigate,
		checkDuplicateNurseAsync,
		profileMode,
		chosenDepartment.department_id,
		preEditNurseData.jobIds,
		refreshRequests,
		showErrorSnackbar,
		saveProfileEdits,
		track,
	]);

	const handleJobChangeCancel = useCallback(() => setPendingJobChange(null), []);

	const handleJobChangeApply = useCallback(async () => {
		if (!pendingJobChange) return;
		const { removedJobIds, newJobIds, nurseDataToSubmit } = pendingJobChange;
		setIsApplyingJobChange(true);
		try {
			await backendService.updateNurseJobs(chosenDepartment.department_id, nurseData.id, {
				new_job_ids: newJobIds,
				reassign_shifts_to_primary: true,
			});
			track("nurse_job_change", { removedJobCount: removedJobIds.length, reassignedShifts: true });
			await refreshRequests();
			setPendingJobChange(null);
			await saveProfileEdits(nurseDataToSubmit);
		} catch (e) {
			// Dialog stays open — a sibling-facility shift can still block the change.
			trackErrorEvent("nurse_job_change_error", { nurseId: nurseData.id, error: String(e) });
			showErrorSnackbar("error.general", { nurseId: nurseData.id }, e);
		} finally {
			setIsApplyingJobChange(false);
		}
	}, [pendingJobChange, chosenDepartment.department_id, nurseData.id, refreshRequests, saveProfileEdits, showErrorSnackbar, track]);

	const handleImageChanged = useCallback((file: File) => {
		setChangedImage(file);
	}, []);

	/**
	 * Edits the form holds that are not saved yet. Serialized comparison because the form state is a deep
	 * object edited field by field; Luxon dates serialize to ISO, so an untouched date compares equal.
	 */
	const hasUnsavedEdits = useMemo(
		() => profileMode === ProfileMode.EDIT && (changedImage !== null || JSON.stringify(nurseData) !== JSON.stringify(lastSeededNurseData)),
		[profileMode, changedImage, nurseData, lastSeededNurseData],
	);

	/**
	 * A merge rewrote the records under the form, so edit mode ends either way. When the record on screen
	 * was the one deleted, the kept record's profile takes its place; otherwise this one reloads with
	 * everything that moved onto it.
	 */
	const handleNurseMerged = useCallback(
		(result: NurseMergeResult) => {
			setProfileMode(ProfileMode.VIEW);
			if (result.mergedNurseId === nurseData.id) {
				navigate(`/${chosenDepartment.department_id}/nurse/${result.primaryNurseId}`, { replace: true, state: location.state });
				return;
			}
			refetchProfile();
		},
		[nurseData.id, navigate, chosenDepartment.department_id, location.state, refetchProfile],
	);

	const handleSaveClickWithDemoDialog = useCallback(async () => {
		if (isAnyDemo && profileMode === ProfileMode.NEW) {
			const firstName = nurseData.firstName || "[first name]";
			const defaultMessage = `Hi ${firstName} and welcome aboard! 👋
I'm Jennifer, your scheduler. If you have any questions, feel free to reach out anytime at (555) 123-456-78.
Your buddy on the team will be Julie — you can contact her at (555) 987-654-32.
Looking forward to working with you!`;
			setWelcomeMessage(defaultMessage);
			setShowWelcomeDialog(true);
		} else {
			await handleSaveClick();
		}
	}, [isAnyDemo, profileMode, nurseData.firstName, handleSaveClick]);

	const handleWelcomeDialogClose = useCallback(async () => {
		setShowWelcomeDialog(false);
		await handleSaveClick();
	}, [handleSaveClick]);

	const handleDuplicateCancel = useCallback(() => {
		if (duplicateMatches.length > 0) {
			trackEvent("duplicate_nurse_warning_dismissed", {
				matched_nurse_ids: duplicateMatches.map((m) => m.id),
				count: duplicateMatches.length,
			});
		}
		setDuplicateMatches([]);
	}, [duplicateMatches]);

	const handleDuplicateConfirmCreate = useCallback(async () => {
		if (!duplicateMatches.length) return;
		trackEvent("duplicate_nurse_warning_confirmed", {
			matched_nurse_ids: duplicateMatches.map((m) => m.id),
			count: duplicateMatches.length,
		});
		setDuplicateMatches([]);

		const nurseDataToSubmit = getSanitizedNurseData(nurseData);
		if (!nurseDataToSubmit) return;

		await createNurseAndNavigate(nurseDataToSubmit);
	}, [duplicateMatches, nurseData, createNurseAndNavigate]);

	return {
		nurseData,
		setNurseData,
		profileMode,
		imageDataString,
		isEditable,
		handleOnBackClick,
		backLabel,
		onEditClick,
		toggleEditCancelWarning,
		showEditCancelWarning,
		handleEditCancelConfirmed,
		onDeletePtoConfirmed,
		isDeletingTimeOff,
		isLoadingProfile,
		isProfileHydrating,
		refetchProfile,
		isAddingNurseLoading,
		isEditNurseLoading,
		createUserWhenCreatingNurse,
		setCreateUserWhenCreatingNurse,
		setDisableSaveDueToAnIssueCreatingUser,
		isFormValid,
		isAnyDemo,
		chosenDepartment,
		handleImageChanged,
		isPostProfileImageLoading,
		showWelcomeDialog,
		welcomeMessage,
		setWelcomeMessage,
		handleSaveClickWithDemoDialog,
		handleWelcomeDialogClose,
		pendingJobChange,
		handleJobChangeCancel,
		handleJobChangeApply,
		isApplyingJobChange,
		duplicateMatches,
		handleDuplicateCancel,
		handleDuplicateConfirmCreate,
		hasUnsavedEdits,
		handleNurseMerged,
	};
};
